import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';
import useEmblaCarousel from 'embla-carousel-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { useCollections } from '@/hooks/useProducts';

import collectionMountain from '@/assets/collection-mountain.jpg';
import collectionFauna from '@/assets/collection-fauna-home.jpg';
import collectionEquestrian from '@/assets/collection-equestrian-home.jpg';

// Map Shopify handles to URL slugs and fallback images
const handleToSlug: Record<string, { slug: string; image: string; key: string }> = {
  'collezione-montagna': { slug: 'mountain', image: collectionMountain, key: 'mountain' },
  'collezione-fauna': { slug: 'fauna', image: collectionFauna, key: 'fauna' },
  equitazione: { slug: 'equestrian', image: collectionEquestrian, key: 'equestrian' },
};

const displayOrder = ['collezione-montagna', 'collezione-fauna', 'equitazione'];

export function CollectionsOverview() {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [emblaRef] = useEmblaCarousel({ align: 'start', loop: false });
  const { data: shopifyCollections } = useCollections();

  const collections = displayOrder
    .map(handle => {
      const shopify = shopifyCollections?.find(c => c.handle === handle);
      const mapping = handleToSlug[handle];
      if (!mapping) return null;
      return {
        handle,
        slug: mapping.slug,
        key: mapping.key,
        image: mapping.image,
        title: shopify?.title || t(`collections.${mapping.key}.name`),
        description: shopify?.description || t(`collections.${mapping.key}.description`),
      };
    })
    .filter(Boolean) as Array<{ handle: string; slug: string; key: string; image: string; title: string; description: string }>;

  const CollectionCard = ({ collection, index }: { collection: typeof collections[0]; index: number }) => (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.15 }}
    >
      <Link
        to={`/collections/${collection.slug}`}
        className="block group relative overflow-hidden rounded-lg aspect-[3/4] card-hover"
      >
        <img
          src={collection.image}
          alt={`${collection.title} - Handcrafted bronze jewelry collection by Sampaoli`}
          className="absolute inset-0 w-full h-full object-cover img-zoom"
          style={collection.key === 'fauna' ? { objectPosition: '60% 15%' } : undefined}
          loading="lazy"
          width={600}
          height={800}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-charcoal/80 via-charcoal/20 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          <h3 className="font-heading text-2xl md:text-3xl font-semibold text-ivory mb-2">
            {collection.title}
          </h3>
          <p className="font-body text-sm md:text-base text-cream/80 mb-4 line-clamp-2">
            {collection.description}
          </p>
          <span className="inline-flex items-center gap-2 font-body text-sm text-bronze-light group-hover:gap-3 transition-all">
            {t('collections.viewCollection')}
            <ArrowRight className="w-4 h-4" />
          </span>
        </div>
      </Link>
    </motion.div>
  );

  return (
    <section className="section-padding bg-secondary/50">
      <div className="container-wide">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <span className="font-body text-xs md:text-sm uppercase tracking-[0.3em] text-bronze mb-4 block">
            {t('collections.title')}
          </span>
          <h2 className="font-elegant text-3xl md:text-4xl lg:text-5xl text-foreground mb-4 italic">
            {t('collections.subtitle')}
          </h2>
          <p className="font-body text-sm md:text-base text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {t('collections.intro')}
          </p>
        </motion.div>

        {isMobile ? (
          <div className="overflow-hidden" ref={emblaRef}>
            <div className="flex gap-4">
              {collections.map((collection, index) => (
                <div key={collection.handle} className="flex-none basis-[85%]">
                  <CollectionCard collection={collection} index={index} />
                </div>
              ))}
            </div>
            <p className="text-center text-sm text-muted-foreground mt-4">
              {t('featured.swipeToSee')}
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8">
            {collections.map((collection, index) => (
              <CollectionCard key={collection.handle} collection={collection} index={index} />
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
